import { useEffect, useState } from "react";
import TextFieldAtom from "../../../../components/atoms/text-field/text-field";

/**
 * Editable description cell - blur-commit architecture.
 * Local state drives the input for smooth typing; the value is only flushed
 * to the parent (rows state) on blur or Enter, never on every keystroke.
 * This prevents auto-save from firing mid-word and overwriting in-progress text.
 */
export const EditableDescriptionCell = ({
	row,
	isEditing,
	onRowChange,
	onEditEnd,
	editInputRef,
	persistOnlyOnBlur = false,
	placeholder,
}) => {
	const [value, setValue] = useState(row.original.shortText ?? "");

	// Reset local state when entering edit mode or switching rows.
	useEffect(() => {
		if (isEditing) {
			setValue(row.original.shortText ?? "");
		}
	}, [isEditing, row.original.id, row.original.shortText]);

	if (!isEditing) {
		const display = row.original.shortText ?? "";
		if (!display) return null;
		return <span className="boq-grid-short-text">{display}</span>;
	}

	/** Commit current local value to the rows tree - only if it actually changed. */
	const commit = (v) => {
		if (v === (row.original.shortText ?? "")) return;
		onRowChange?.(row.original.id, { shortText: v });
	};

	return (
		<div
			onClick={(e) => e.stopPropagation()}
			className="boq-grid-edit-cell boq-grid-short-text-input-wrapper"
		>
			<TextFieldAtom
				ref={editInputRef}
				type="text"
				value={value}
				placeholder={placeholder}
				onChange={(e) => {
					// Update local display state only - no parent flush yet.
					setValue(e.target.value);
				}}
				onBlur={() => {
					// Flush to parent on every blur, regardless of persistOnlyOnBlur.
					commit(value);
					if (!persistOnlyOnBlur) onEditEnd?.();
				}}
				onKeyDown={(e) => {
					e.stopPropagation();
					if (e.key === "Enter") {
						e.preventDefault();
						commit(value);
						if (!persistOnlyOnBlur) onEditEnd?.();
					}
				}}
			/>
		</div>
	);
};

/**
 * Editable number cell - blur-commit architecture.
 * Local state drives the input for smooth typing; the parsed value is only
 * flushed to the parent on blur, never on every keystroke.
 *
 * Optional `value` / `onChange` props override the default field-based access.
 * Use these when the displayed scale differs from the stored value
 * (e.g. taxRate stored as 0.19 but edited as 19%).
 */
export const EditableNumberCell = ({
	row,
	field,
	onRowChange,
	decimalPlaces,
	suffix = "",
	value: valueProp,
	onChange: onChangeProp,
	/** Optional: fires with the parsed value on every keystroke (for live display of derived fields). */
	onDraftChange,
}) => {
	const raw = valueProp !== undefined ? valueProp : row?.original?.[field];
	const initialValue = raw != null && raw !== "" ? String(raw) : "";
	const [value, setValue] = useState(initialValue);
	const fieldValue = valueProp !== undefined ? valueProp : row?.original?.[field];

	// Reset local state when switching rows or field (or external value prop)
	useEffect(() => {
		setValue(fieldValue != null && fieldValue !== "" ? String(fieldValue) : "");
	}, [row?.original?.id, field, fieldValue]);

	const parseAndPersist = (v) => {
		const num =
			v === "" ? null : parseFloat(String(v).replace(",", "."));
		const parsed = num !== null && !Number.isNaN(num) ? num : null;

		// Skip if value hasn't actually changed - avoids unnecessary saves.
		const original = valueProp !== undefined ? valueProp : row?.original?.[field];
		const origNum = original != null && original !== ""
			? parseFloat(String(original).replace(",", "."))
			: null;
		if (parsed === origNum || (parsed == null && origNum == null)) return;

		if (onChangeProp) {
			onChangeProp(parsed);
		} else {
			onRowChange?.(row.original.id, { [field]: parsed });
		}
	};

	return (
		<div
			onClick={(e) => e.stopPropagation()}
			onKeyDown={(e) => e.stopPropagation()}
			className="boq-grid-edit-cell"
		>
			<TextFieldAtom
				type="number"
				value={value}
				onChange={(e) => {
					const v = e.target.value;
					setValue(v);
					// Fire parsed value immediately for live-derived fields (e.g. totals).
					if (onDraftChange) {
						const num = v === "" ? null : parseFloat(String(v).replace(",", "."));
						onDraftChange(num !== null && !Number.isNaN(num) ? num : null);
					}
				}}
				onBlur={() => {
					// Commit parsed value to parent on blur.
					parseAndPersist(value);
				}}
				decimalPlaces={decimalPlaces}
				suffix={suffix}
				className="text-end"
			/>
		</div>
	);
};

/**
 * Editable OZ (Ordnungszahl / item number) cell - blur-commit architecture.
 *
 * Renders a plain text input so the user can override the auto-generated OZ.
 * On blur/Enter the new value is committed via onRowChange, which triggers
 * the cascade logic (group/subgroup prefix propagation) in the editor page.
 * Escape discards the in-progress edit.
 */
export const EditableOZCell = ({ row, onRowChange, hasError }) => {
	const [value, setValue] = useState(row.original.itemNumber ?? "");

	// Sync when the row switches or the auto-generated OZ updates.
	useEffect(() => {
		setValue(row.original.itemNumber ?? "");
	}, [row.original.id, row.original.itemNumber]);

	const commit = (v) => {
		const trimmed = v.trim();
		if (trimmed === (row.original.itemNumber ?? "")) return;
		onRowChange?.(row.original.id, {
			itemNumber: trimmed === "" ? null : trimmed,
		});
	};

	return (
		<div
			onClick={(e) => e.stopPropagation()}
			className="boq-grid-edit-cell"
		>
			<input
				type="text"
				value={value}
				className={`boq-oz-input${hasError ? " boq-oz-input--error" : ""}`}
				onChange={(e) => setValue(e.target.value)}
				onBlur={() => commit(value)}
				onKeyDown={(e) => {
					e.stopPropagation();
					if (e.key === "Enter") {
						e.preventDefault();
						commit(value);
					}
					if (e.key === "Escape") {
						setValue(row.original.itemNumber ?? "");
					}
				}}
			/>
		</div>
	);
};
